// lib/productTags.ts
// The coloured labels a shop puts on its own product cards (bazaar's feed
// `<tags>`, parsed by parseFeedTags in skroutzXml.ts). Pure helpers, unit-tested;
// the card row itself is components/search/ProductGrid/ProductTags.tsx.
//
// Shown only when the client turns `ui.showProductTags` on (bazaar, 2026-10-09).

export interface ProductTag {
  type: 'tag' | 'badge';
  label: string;
  bg?: string;
  color?: string;
  image?: string;
}

/**
 * Most useful first — the founder's order (2026-10-09). The card shows one row,
 * so whatever does not fit collapses into «+N»; this order decides what stays.
 * Matched on the accent-free, lower-case label, by "contains".
 */
const PRIORITY: string[][] = [
  ['δωρο'],                                    // 1+1 / 2+1 ΔΩΡΟ
  ['προσφορα'],
  ['φυλλαδιου'],                               // ΠΡΟΪΟΝ ΦΥΛΛΑΔΙΟΥ
  ['νεο προιον'],
  ['τηλεοπτικου'],                             // Προϊόν Τηλεοπτικού
  ['best seller'],
  ['γλουτενη', 'λακτοζη', 'φυτικο', 'βιολογικο', 'vegan'],
  ['κοπης', 'foodservice'],
];

function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/ς/g, 'σ');
}

export function tagRank(label: string): number {
  const f = fold(label);
  const i = PRIORITY.findIndex((group) => group.some((k) => f.includes(fold(k))));
  return i === -1 ? PRIORITY.length : i;
}

/** Text labels in priority order (stable for equal ranks). Image labels are separate. */
export function sortedTextTags(tags: ProductTag[] | undefined): ProductTag[] {
  return (tags ?? [])
    .filter((t) => !t.image && t.label)
    .map((t, i) => ({ t, i, r: tagRank(t.label) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.t);
}

/** Labels the shop draws as a picture (Μείωση Τιμής, Μόνο στα Bazaar). */
export function imageTags(tags: ProductTag[] | undefined): ProductTag[] {
  return (tags ?? []).filter((t) => !!t.image);
}

/** Greek capitals the way they are written: «Χωρίς» → «ΧΩΡΙΣ», keeping «Ϊ». */
export function upperGreek(label: string): string {
  return label
    .toLocaleUpperCase('el-GR')
    .normalize('NFD')
    .replace(/́/g, '')
    .normalize('NFC');
}

function luminance(hex: string): number | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

export const DARK_LABEL_TEXT = '#1A1A1A';

/**
 * The shop's own text colour, unless it is white on a background so pale it
 * can't be read: then dark text (founder, 2026-10-09). Threshold 2:1 changes
 * exactly the two pale bazaar labels — ΠΡΟΪΟΝ ΦΥΛΛΑΔΙΟΥ #FEC10E (1.7:1) and
 * ΝΕΟ ΠΡΟΪΟΝ #9AD2AD (1.8:1) — and keeps every other label as their site shows it.
 */
export function readableTextColor(bg: string | undefined, color: string | undefined): string {
  const text = color || '#FFFFFF';
  const lb = bg ? luminance(bg) : null;
  const lt = luminance(text);
  if (lb === null || lt === null) return text;
  const [hi, lo] = lb > lt ? [lb, lt] : [lt, lb];
  const contrast = (hi + 0.05) / (lo + 0.05);
  return contrast < 2 ? DARK_LABEL_TEXT : text;
}
